// Freemis hjemmeside (pixie-domænet): virksomheder, der bruger Freemi (referencer), anmeldelser og henvendelser.
// Regler: kun rigtige kunder og rigtige udtalelser. En anmeldelse vises først, når Freemi har godkendt den, og kun med
// kundens samtykke. Logoer uploades af Freemi (kundens eget logo) – ellers vises navnet som tekst.
// Samme regler i databasen (supabase/migrations/*_hjemmeside.sql).

export type Reference = { id: string; navn: string; url: string; logo: string | null; vis: boolean; raekke: number };
export type AnmeldelseStatus = 'afventer' | 'godkendt' | 'afvist';
export type Anmeldelse = {
  id: string; navn: string; rolle: string; firma: string; tekst: string; stjerner: number;
  status: AnmeldelseStatus; kilde: 'kunde' | 'pixie'; samtykke: boolean; oprettet: string; klient_slug: string | null;
};
/** Det, der vises offentligt (ingen interne felter). */
export type AnmeldelseOffentlig = Pick<Anmeldelse, 'id' | 'navn' | 'rolle' | 'firma' | 'tekst' | 'stjerner'>;
export type Henvendelse = { id: string; navn: string; firma: string; email: string; telefon: string; besked: string; oprettet: string; behandlet: boolean };
export type NyHenvendelse = Pick<Henvendelse, 'navn' | 'firma' | 'email' | 'telefon' | 'besked'> & { web?: string };   // web = honningkrukke (skal være tom)
export type NyAnmeldelse = Pick<Anmeldelse, 'navn' | 'rolle' | 'tekst' | 'stjerner' | 'samtykke'>;

/** Virksomheder, der bruger Freemi (Armin, oktober 2026). Rigtige logoer uploades i konsollen. */
export const REFERENCER_START: Omit<Reference, 'id'>[] = [
  { navn: 'SteamGreen', url: 'https://steamgreen.dk' },
  { navn: 'Bring & Clean', url: '' },
  { navn: 'We Do Sales', url: 'https://wedosales.dk' },
  { navn: 'XIIA', url: 'https://xiia.dk' },
  { navn: 'SteamFox', url: 'https://steamfox.dk' },
  { navn: 'Bilpleje Bilrens', url: 'https://bilplejebilrens.dk' },
  { navn: 'tæpperens.dk', url: 'https://tæppe-rens.dk' },
  { navn: 'sofarens.dk', url: 'https://sofarens.dk' },
  { navn: 'SteamBilvask', url: '' },
  { navn: 'SteamClean', url: 'https://steamcleaner.dk' },
].map((r, i) => ({ ...r, logo: null, vis: true, raekke: i }));

export const GRAENSER = { henvendelse_pr_email_time: 3, henvendelser_10min: 30, anmeldelse_vent_sek: 60, logo_bytes: 300_000 };
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const rens = (s: string | null | undefined, n: number) => (s ?? '').replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, n);
const rensTekst = (s: string | null | undefined, n: number) => (s ?? '').replace(/[\u0000-\u0009\u000b-\u001f\u007f]/g, '').trim().slice(0, n);

export function tjekHenvendelse(h: NyHenvendelse): { felter: Record<string, string>; ren: Omit<NyHenvendelse, 'web'>; spam: boolean } {
  const ren = { navn: rens(h.navn, 80), firma: rens(h.firma, 80), email: rens(h.email, 120).toLowerCase(), telefon: rens(h.telefon, 20), besked: rensTekst(h.besked, 2000) };
  const f: Record<string, string> = {};
  if (ren.navn.length < 2) f.navn = 'Skriv dit navn.';
  if (!EMAIL.test(ren.email)) f.email = 'Skriv en gyldig e-mail.';
  if (ren.telefon && !/^\+?[\d\s-]{8,16}$/.test(ren.telefon)) f.telefon = 'Skriv et gyldigt telefonnummer – eller lad feltet stå tomt.';
  if (ren.besked.length < 10) f.besked = 'Skriv lidt mere, så vi kan hjælpe.';
  return { felter: f, ren, spam: !!h.web?.trim() };
}

export function tjekAnmeldelse(a: NyAnmeldelse): { felter: Record<string, string>; ren: NyAnmeldelse } {
  const ren = { navn: rens(a.navn, 80), rolle: rens(a.rolle, 80), tekst: rensTekst(a.tekst, 600), stjerner: Math.round(Number(a.stjerner)), samtykke: !!a.samtykke };
  const f: Record<string, string> = {};
  if (ren.navn.length < 2) f.navn = 'Skriv dit navn.';
  if (ren.tekst.length < 20) f.tekst = 'Skriv mindst et par sætninger.';
  if (!(ren.stjerner >= 1 && ren.stjerner <= 5)) f.stjerner = 'Vælg 1–5 stjerner.';
  if (!ren.samtykke) f.samtykke = 'Sæt flueben, hvis vi må vise anmeldelsen på hjemmesiden.';
  return { felter: f, ren };
}

export function tjekReference(r: Pick<Reference, 'navn' | 'url' | 'logo'>): Record<string, string> {
  const f: Record<string, string> = {};
  if (rens(r.navn, 80).length < 2) f.navn = 'Skriv virksomhedens navn.';
  if (r.url && !/^https:\/\/[^\s/]+\.[^\s]+$/u.test(r.url.trim())) f.url = 'Skriv adressen med https://';
  if (r.logo && !/^data:image\/(png|jpeg|webp|svg\+xml);base64,/.test(r.logo) && !/^https:\/\//.test(r.logo)) f.logo = 'Logoet skal være PNG, JPG, WebP eller SVG.';
  if (r.logo?.startsWith('data:') && Math.ceil(r.logo.length * 3 / 4) > GRAENSER.logo_bytes) f.logo = 'Logoet må højst fylde 300 KB.';
  return f;
}

/** Til visning: kun synlige referencer i den valgte rækkefølge og kun godkendte anmeldelser med samtykke. */
export function offentligHjemmeside(r: Reference[], a: Anmeldelse[]): { referencer: Reference[]; anmeldelser: AnmeldelseOffentlig[] } {
  return {
    referencer: r.filter((x) => x.vis).sort((x, y) => x.raekke - y.raekke || x.navn.localeCompare(y.navn)),
    anmeldelser: a.filter((x) => x.status === 'godkendt' && x.samtykke).sort((x, y) => y.oprettet.localeCompare(x.oprettet))
      .map(({ id, navn, rolle, firma, tekst, stjerner }) => ({ id, navn, rolle, firma, tekst, stjerner })),
  };
}
